import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {supplierClusterMemberCreateUrl, supplierClusterMembersByEntityUrl} from "~/pages/supplier-360/services/supplier360Urls";
import type {SupplierClusterMember, SupplierClusterMemberCreate} from "~/pages/supplier-360/types/Supplier360Types";

/**
 * `POST supplier_360/cluster_member/` — docs §2.2.
 *
 * Wired to the Cluster section's "Add new cluster" row (2026-09-14). Note the limit of what it
 * can do: it creates a **new** manual row carrying the picked name, with a server-generated
 * `manual-{hex}` `s_id`. It does not move the raw row that was picked — `s_id` is
 * read-only and `split` only removes — so that row stays in its own cluster, and this one has no
 * transactions behind it to contribute to the entity's spend or business-unit rollups (§2.2, gap 1).
 *
 * Invalidates every `cluster_member` query, not just this entity's: the registry's Raw view lists
 * the same rows under a different URL, so a row added here has to show up there too.
 */
export function useCreateSupplierClusterMemberMutation() {
    const queryClient = useQueryClient();

    return useMutation<SupplierClusterMember, DefaultError, SupplierClusterMemberCreate>({
        mutationFn: async (payload) => {
            const {data} = await queryHttp.post<SupplierClusterMember>(
                supplierClusterMemberCreateUrl(),
                payload,
            );
            return data;
        },
        onSuccess: (created, {entity_id}) => {
            // Header spend is not stale after this save. The new row adds no spend, so the entity
            // total, the KPI strip, and the spend charts are still right if they stay cached. Those
            // queries would need a refresh only after a real move, which this endpoint does not do.
            void queryClient.invalidateQueries({queryKey: [supplierClusterMembersByEntityUrl(entity_id)]});
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === 'string' && key.startsWith('supplier_360/cluster_member');
                },
            });
            notificationStore.show(`${created.normalized_name} added to the cluster.`, {
                severity: 'success',
                id: `cluster-member-add-success-${entity_id}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {entity_id}) => {
            notificationStore.show('Could not add the raw row. Please try again.', {
                severity: 'error',
                id: `cluster-member-add-error-${entity_id}`,
                autoHideDuration: 4000,
            });
        },
    });
}
